import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  withBlueSteelTheme,
  Modal,
  Feedback,
  Toast,
  RectangleLayout,
  Portal,
} from '../../components';
import * as ROUTES from '../../constants/routes';
import { useReqTimeout, useBrowserUnloadActions } from './customHooks';
import { EscalationScan, New_EscalationScan } from '../../assets/img';
import * as styles from './styles.css';
import '../../global/typography.css';
import {
  REQUEST_STATES,
  ASSOCIATE_CONFIRMATION_ACTIONS,
  LABELS,
  QR_LOCATIONS,
  KEY_FEEDBACK_SUBMITTED,
} from '../../constants/constants';
import {
  SmallIconMap,
  getCoverImageMap,
  getTitleAndSupportiveText,
} from './utils';
import {
  preparePayloadForStorage,
  setValueInBrowserStorage,
  getQueryParam,
  doesStringMatch,
  submitFeedback,
  getInitialRequestTimestamp,
  prepareStateObj,
  getValueFromBrowserStorage,
} from '../../utils';
import { useAssociateAckDetails } from '../../hooks/useAssociateAckDetails';
import useFeedbackFlow from '../shared/useFeedbackFlow';

const HelpOnTheWay = (props) => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const prevRequestPayloadIfAny = location.state ?? {};
  const {
    isDuplicateRequest,
    qrId,
    transactionId,
    eventTimestamp,
  } = prevRequestPayloadIfAny;
  const feedbackSubmitted = !!getValueFromBrowserStorage(KEY_FEEDBACK_SUBMITTED);

  const [requestState, setRequestState] = useState(() => {
    const stateFromLocation = prevRequestPayloadIfAny?.requestState;
    const matchedState = Object.values(REQUEST_STATES).find((s) => s === stateFromLocation);
    if (isDuplicateRequest && matchedState) {
      return matchedState;
    }
    return REQUEST_STATES.Pending;
  });
  const [didAssociateMeet, setDidAssociateMeet] = useState(false);
  const [isNotYetOrNo, setIsNotYetOrNo] = useState(false);

  const shouldShowReqPendingModal = (requestState === REQUEST_STATES.Pending || requestState === REQUEST_STATES.Escalated);
  const [showReqPendingModal, setShowReqPendingModal] = useState(() => shouldShowReqPendingModal && isDuplicateRequest);

  const getLocaleStr = (id) => intl.formatMessage({ id });
  const qrLocation = getQueryParam(location.search, LABELS.LOCATION);
  const isBackroom = doesStringMatch(qrLocation, QR_LOCATIONS.Backroom);

  const REQUEST_STATE_AFTER_ACK = isBackroom ? REQUEST_STATES.Trck_Delivery_On_The_Way : REQUEST_STATES.On_The_Way;

  const {
    active,
    setActive,
    showFeedbackModal,
    setShowFeedbackModal,
    rating,
    feedbackListOptions,
    feedbackReaction,
    isFeedbackSubmitted,
    onRatingChange,
    resetFeedbackData,
    onFeedbackSubmit,
  } = useFeedbackFlow({
    isCA: props.isCA,
    isBackroom,
    getLocaleStr,
    initialFeedbackSubmitted: feedbackSubmitted,
    submitFeedbackHandler: (starRating, cannedResponses, feedbackText, metFlag) => submitFeedback(
      qrId,
      transactionId,
      starRating,
      eventTimestamp,
      cannedResponses,
      feedbackText,
      metFlag,
    ),
  });

  /** Custom Hooks */
  const [associateName] = useAssociateAckDetails(transactionId, setRequestState, REQUEST_STATE_AFTER_ACK);
  const previousState = useReqTimeout(requestState, setRequestState, prevRequestPayloadIfAny?.timestamp);
  useBrowserUnloadActions(history, location, qrId, transactionId);
  /** ------------ */

  const { titleText, renderSupportiveText } = getTitleAndSupportiveText(
    getLocaleStr,
    requestState,
    associateName,
    qrLocation,
    qrId,
    props.isCA,
  );

  useEffect(() => {
    const timestamp = getInitialRequestTimestamp(prevRequestPayloadIfAny?.timestamp, isDuplicateRequest);
    const payload = preparePayloadForStorage(qrLocation, requestState, transactionId, timestamp, eventTimestamp, associateName);
    setValueInBrowserStorage(qrId, payload, localStorage);
  }, [requestState, qrLocation, transactionId, eventTimestamp, associateName, qrId, prevRequestPayloadIfAny?.timestamp, isDuplicateRequest]);

  useEffect(() => {
    setValueInBrowserStorage(KEY_FEEDBACK_SUBMITTED, isFeedbackSubmitted);
  }, [isFeedbackSubmitted]);

  useEffect(() => {
    if (!props.isCA) return;

    if (requestState === REQUEST_STATES.CustomerMeet) {
      setDidAssociateMeet(false);
      setIsNotYetOrNo(true);
      return;
    }

    if (
      requestState === REQUEST_STATES.On_The_Way
      || requestState === REQUEST_STATES.Trck_Delivery_On_The_Way
    ) {
      const timeoutValue = previousState === REQUEST_STATES.CustomerMeet ? 0 : 50000;
      const timerId = setTimeout(() => setDidAssociateMeet(true), timeoutValue);
      return () => clearTimeout(timerId);
    }

    if (requestState === REQUEST_STATES.Still_Looking) {
      setDidAssociateMeet(false);
    }
  }, [requestState, previousState, props.isCA]);

  const userAction = (action, starRating = null, cannedResponses = null, feedbackText = null) => {
    if (!props.isCA) return; // Only for isCA

    if (action === ASSOCIATE_CONFIRMATION_ACTIONS.NotYet) {
      setRequestState(REQUEST_STATES.CustomerMeet);
      submitFeedback(qrId, transactionId, starRating, eventTimestamp, cannedResponses, feedbackText, false);
    } else if (action === ASSOCIATE_CONFIRMATION_ACTIONS.No) {
      setRequestState(REQUEST_STATES.Still_Looking);
      submitFeedback(qrId, transactionId, starRating, eventTimestamp, cannedResponses, feedbackText, false);
    } else {
      history.push(ROUTES.THANKS, prepareStateObj(qrId, false, { transactionId, eventTimestamp }));
      submitFeedback(qrId, transactionId, starRating, eventTimestamp, cannedResponses, feedbackText, true);
    }
  };

  const receivingHoursText = isBackroom ? getLocaleStr('receiving_hours') : '';
  const imageMap = getCoverImageMap(props.isCA);

  return (
    <div className="column rect-container">
      <RectangleLayout
        smallIcon={SmallIconMap[requestState]}
        titleText={titleText}
        renderSupportiveText={renderSupportiveText}
        coverImage={imageMap[requestState]}
        belowText={receivingHoursText}
        isCA={props.isCA}
        {...(props.isCA && {
          getLocaleStr,
          userAction,
          didAssociateMeet,
          setDidAssociateMeet,
          requestState,
          isNotYetOrNo,
          setIsNotYetOrNo,
        })}
      >
        {((props.isCA ? (requestState === REQUEST_STATES.Still_Looking && !isFeedbackSubmitted) : (requestState !== REQUEST_STATES.Pending && !isFeedbackSubmitted))) && (
          <div className="feedback-container">
            <Feedback
              titleStyle="typography-regular font-400 mb-12"
              title={props.isCA ? getLocaleStr('thanks_new_feedback_title') : getLocaleStr('give_feedback')}
              starSize="20"
              numberOfStars={5}
              getLocaleStr={getLocaleStr}
              onClick={() => setShowFeedbackModal(true)}
              isCA={props.isCA}
            />
          </div>
        )}
      </RectangleLayout>
      {/* <button type="button" className="action-button" onClick={handleOnActionPress}>{Contents.IAMHERE_BUTTON}</button> */}
      {/* Todo: Update this later once integration is done */}
      {showReqPendingModal && (
        <Portal>
          <div className="font-500 portal-modal__container">
            <img src={props.isCA ? New_EscalationScan : EscalationScan} alt="finding_help-icon" className="portal-modal__icon" />
            {!props.isCA && (
            <div className="roboto typography-regular font-400 portal-modal__text">
              <span>{getLocaleStr('still_finding_help')}</span>
            </div>
            )}
            <button
              type="button"
              className="font-500 portal-modal__button"
              onClick={() => setShowReqPendingModal(false)}
            >
              {getLocaleStr('buttons.ok')}
            </button>
          </div>
        </Portal>
      )}
      {showFeedbackModal && (
        <Modal
          size="stretch"
          title={getLocaleStr('give_feedback')}
          onClose={resetFeedbackData}
          isCA={props.isCA}
        >
          <div className="scroll-content-modal">
            <Feedback
              allowUserSelection
              titleStyle="typography-medium font-500 mb-16"
              starSize="46"
              title={getLocaleStr('how_was_exp')}
              getLocaleStr={getLocaleStr}
              numberOfStars={5}
              onFeedbackSubmit={onFeedbackSubmit}
              feedbackListOptions={feedbackListOptions}
              feedbackReaction={feedbackReaction}
              onRatingSelect={(selected) => onRatingChange(selected)}
              isCA={props.isCA}
            />
            {/* Todo - Add real content to feedback */}
          </div>
        </Modal>
      )}
      {active && <Toast message={getLocaleStr(`feedback_response_${rating > 3 ? 'good' : 'bad'}`)} onHide={setActive} isCA={props.isCA} />}
    </div>
  );
};

HelpOnTheWay.propTypes = {
  isCA: PropTypes.bool.isRequired,
};

export default withBlueSteelTheme(styles)(HelpOnTheWay);
